export const COLOR_SOCIO_NEUTRAL = "#64748B";
export const RADIO_COBERTURA_DEFAULT = 2000;
export const ZOOM_MINIMO_COBERTURA = 11;

const HEX_COLOR_PATTERN = /^#[0-9A-Fa-f]{6}$/;

const SOCIO_STATUS_LABELS = {
  SIN_SOCIO: "Sin socio asignado",
  FORMATO_SOCIO_INVALIDO: "Socio con formato inválido",
  SOCIO_INEXISTENTE: "Socio inexistente",
  SOCIO_SIN_COLOR: "Socio sin color configurado",
  SOCIO_INCONSISTENTE_EN_JORNADA: "Socio inconsistente en la jornada",
};

export const normalizeMapColor = (value) => {
  const color = String(value || "").trim();
  return HEX_COLOR_PATTERN.test(color) ? color.toUpperCase() : COLOR_SOCIO_NEUTRAL;
};

const getRgbChannels = (color) => {
  const normalized = normalizeMapColor(color);
  return [1, 3, 5].map((index) => parseInt(normalized.slice(index, index + 2), 16));
};

export const getRelativeLuminance = (color) => {
  const [red, green, blue] = getRgbChannels(color).map((channel) => {
    const value = channel / 255;
    return value <= 0.03928 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
  });

  return 0.2126 * red + 0.7152 * green + 0.0722 * blue;
};

export const isLightMapColor = (color) => getRelativeLuminance(color) >= 0.72;

export const getContrastStrokeColor = (color) => (isLightMapColor(color) ? "#334155" : "#FFFFFF");

export const getContrastTextColor = (color) => (isLightMapColor(color) ? "#111827" : "#FFFFFF");

export const normalizeCoverageRadius = (value) => {
  const radius = Number(value);
  return Number.isFinite(radius) && radius > 0 ? radius : RADIO_COBERTURA_DEFAULT;
};

export const formatCoverageDistance = (value) => {
  const kilometers = normalizeCoverageRadius(value) / 1000;
  return `${Number.isInteger(kilometers) ? kilometers : Number(kilometers.toFixed(2))} km`;
};

export const normalizeSocioStatus = (value) => String(value || "").trim().toUpperCase();

export const getSocioStatusLabel = (value) =>
  SOCIO_STATUS_LABELS[normalizeSocioStatus(value)] || "Socio no determinado";

export const getSocioDisplayLabel = (point = {}) => {
  const status = normalizeSocioStatus(point.estadoSocioMapa);
  const name = String(point.nombreSocio || "").trim();
  const abbreviation = String(point.abreviaturaSocio || "").trim();

  if (status === "RESUELTO") {
    if (abbreviation && name) return `${abbreviation} — ${name}`;
    return name || abbreviation || "Socio no determinado";
  }

  return getSocioStatusLabel(status);
};

export const getSocioLegendKey = (point = {}) => {
  if (point.idSocio !== null && point.idSocio !== undefined && String(point.idSocio).trim()) {
    return `socio:${point.idSocio}`;
  }

  return `estado:${normalizeSocioStatus(point.estadoSocioMapa) || "LEGACY"}:color:${normalizeMapColor(
    point.colorMapa
  )}`;
};

export const buildSociosLegend = (points = []) => {
  const entries = new Map();

  (Array.isArray(points) ? points : []).forEach((point) => {
    const key = getSocioLegendKey(point);
    const current = entries.get(key);

    if (current) {
      current.cantidadPuntos += 1;
      return;
    }

    entries.set(key, {
      key,
      idSocio: point?.idSocio ?? null,
      nombreSocio: point?.nombreSocio ?? null,
      abreviaturaSocio: point?.abreviaturaSocio ?? null,
      colorMapa: normalizeMapColor(point?.colorMapa),
      estadoSocioMapa: normalizeSocioStatus(point?.estadoSocioMapa),
      label: getSocioDisplayLabel(point),
      cantidadPuntos: 1,
    });
  });

  return Array.from(entries.values()).sort((a, b) => a.label.localeCompare(b.label, "es"));
};

export const buildSocioPointSignature = (point = {}) =>
  [
    point.id ?? "",
    point.idConvocatoria ?? "",
    point.id_convocatoria ?? "",
    point.lat ?? point.latitud ?? "",
    point.lng ?? point.longitud ?? "",
    point.totalAtenciones ?? "",
    point.idSocio ?? "",
    point.nombreSocio ?? "",
    point.abreviaturaSocio ?? "",
    point.colorMapa ?? "",
    point.estadoSocioMapa ?? "",
    point.radioCoberturaMetros ?? "",
  ].join(":");

export const isHealthCoverageEnabled = (mode) => mode === "salud";

export const shouldDisplayCoverage = ({ mode, showCoverage, currentZoom, isSelected }) => {
  if (!isHealthCoverageEnabled(mode) || !showCoverage) return false;
  return Number(currentZoom) >= ZOOM_MINIMO_COBERTURA || Boolean(isSelected);
};

export const getCoverageVisualOptions = (color, selected = false) => {
  const fillColor = normalizeMapColor(color);
  const lightColor = isLightMapColor(fillColor);

  return {
    fillColor,
    strokeColor: getContrastStrokeColor(fillColor),
    fillOpacity: selected ? (lightColor ? 0.28 : 0.22) : lightColor ? 0.16 : 0.08,
    strokeOpacity: selected ? 0.95 : 0.55,
    strokeWeight: selected ? 2 : 1,
    zIndex: selected ? 5 : 1,
  };
};
